import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { toaster } from './ui/toast';
import type { UserEstate } from '../api/userEstate';
import { updateUserEstateExpiration } from '../api/userEstate';
import { LOCALE } from '../i18n/format';
import { useT } from '../i18n/admin';
import { estateExpirationDay } from '../utils/userEstateStatus';

interface UserEstateExpirationDateProps {
  userEstate: UserEstate;
  /** Read only: shows the date, no picker. */
  disabled?: boolean;
  /** Names the trigger for screen readers ("Expiration of MARE-25 H5"). */
  label?: string;
}

const pad = (n: number) => String(n).padStart(2, '0');
const toDay = (y: number, m: number, d: number) => `${y}-${pad(m + 1)}-${pad(d)}`;
const todayDay = () => {
  const now = new Date();
  return toDay(now.getFullYear(), now.getMonth(), now.getDate());
};

/**
 * Expiration date of a user_estate (SAM-973): a button that reads "Nov 2, 2026" or "No expiration"
 * and opens a month calendar. Picking a day saves it; "No expiration" clears it. Days are calendar
 * days ('YYYY-MM-DD'), never instants, so the date does not move with the viewer's clock.
 */
export const UserEstateExpirationDate: React.FC<UserEstateExpirationDateProps> = ({ userEstate, disabled = false, label }) => {
  const { t, lang, fmt } = useT();
  const queryClient = useQueryClient();
  const saved = estateExpirationDay(userEstate.expiration_date);
  const [day, setDay] = useState<string | null>(saved);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const initial = saved ?? todayDay();
  const [view, setView] = useState({ y: Number(initial.slice(0, 4)), m: Number(initial.slice(5, 7)) - 1 });
  const wrapRef = useRef<HTMLDivElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: 0, top: 0 });

  // The row is re-read after every save; whatever the server has wins.
  useEffect(() => setDay(saved), [saved]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!wrapRef.current?.contains(target) && !popRef.current?.contains(target)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const today = todayDay();
  const cells = useMemo(() => {
    const first = new Date(view.y, view.m, 1).getDay();
    const count = new Date(view.y, view.m + 1, 0).getDate();
    return [...Array(first).fill(null), ...Array.from({ length: count }, (_, i) => i + 1)] as Array<number | null>;
  }, [view]);
  const monthTitle = new Intl.DateTimeFormat(LOCALE[lang], { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(Date.UTC(view.y, view.m, 1)));
  const weekdays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => new Intl.DateTimeFormat(LOCALE[lang], { weekday: 'short', timeZone: 'UTC' }).format(new Date(Date.UTC(2026, 9, 18 + i))).slice(0, 2)),
    [lang],
  );

  // What the server holds for this row now: the row in the most recently read `/user_estate` list.
  const serverDay = (): string | null | undefined => {
    const lists = queryClient.getQueryCache().findAll({ queryKey: ['/user_estate'], exact: false })
      .sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt);
    for (const q of lists) {
      const rows = (q.state.data as { data?: UserEstate[] } | undefined)?.data;
      const row = Array.isArray(rows) ? rows.find((r) => r.user_id === userEstate.user_id && r.estate_id === userEstate.estate_id) : undefined;
      if (row) return estateExpirationDay(row.expiration_date);
    }
    return undefined;
  };

  const save = async (next: string | null) => {
    setOpen(false);
    if (next === day) return;
    const previous = day;
    setDay(next);
    setSaving(true);
    let sent = true;
    try {
      await updateUserEstateExpiration({
        user_id: userEstate.user_id,
        estate_id: userEstate.estate_id,
        status_id: userEstate.status_id,
        expiration_date: next,
      });
    } catch {
      sent = false;
    }
    // Re-read before saying anything: a 200 does not mean the date changed (QA H1: userEstate ignored
    // `null`, and "No expiration" stayed on screen while the server kept the old date).
    await queryClient.invalidateQueries({ queryKey: ['/user_estate'], exact: false });
    const fresh = serverDay();
    setDay(fresh !== undefined ? fresh : sent ? next : previous);
    setSaving(false);
    if (sent && (fresh === undefined || fresh === next)) {
      toaster.positive(t('userEstate.expiryUpdated'), { autoHideDuration: 2000 });
    } else {
      toaster.negative(t('userEstate.expiryFailed'), { autoHideDuration: 3000 });
    }
  };

  const shown = day ? fmt.date(day) : t('userEstate.noExpiration');

  if (disabled) {
    return <span className={`text-[13px] ${day ? 'text-[var(--ink-700)]' : 'text-[var(--ink-500)]'}`}>{shown}</span>;
  }

  // The popover lives on <body> so a table cell's overflow never clips it.
  const toggle = () => {
    if (!open && wrapRef.current) {
      const r = wrapRef.current.getBoundingClientRect();
      const height = 360;
      setPos({
        left: Math.max(8, Math.min(r.left, window.innerWidth - 298)),
        top: r.bottom + height + 8 > window.innerHeight ? Math.max(8, r.top - height - 4) : r.bottom + 4,
      });
    }
    setOpen((o) => !o);
  };

  const shiftMonth = (delta: number) =>
    setView((v) => {
      const d = new Date(v.y, v.m + delta, 1);
      return { y: d.getFullYear(), m: d.getMonth() };
    });

  return (
    <div ref={wrapRef} className="relative inline-block">
      <button
        type="button"
        aria-label={label ? `${label}: ${shown}` : undefined}
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={saving}
        onClick={toggle}
        className={`inline-flex h-8 items-center gap-2 rounded-lg border border-[var(--ink-300)] bg-white px-2.5 text-[13px] ${day ? 'text-[var(--ink-900)]' : 'text-[var(--ink-500)]'} cursor-pointer disabled:cursor-wait disabled:opacity-60`}
      >
        <CalendarDays size={15} aria-hidden />
        {shown}
      </button>
      {open && createPortal(
        <div ref={popRef} role="dialog" aria-label={label} style={{ left: pos.left, top: pos.top }} className="fixed z-[30000] w-[290px] rounded-2xl bg-white p-5 shadow-[0_4px_20px_rgba(0,0,0,.12)]">
          <div className="mb-3 flex items-center justify-between">
            <button type="button" aria-label={t('userEstate.prevMonth')} onClick={() => shiftMonth(-1)} className="cursor-pointer rounded-md border-0 bg-transparent p-1 text-[var(--ink-500)] hover:bg-[var(--line-subtle)]">
              <ChevronLeft size={16} aria-hidden />
            </button>
            <span className="text-sm font-semibold capitalize">{monthTitle}</span>
            <button type="button" aria-label={t('userEstate.nextMonth')} onClick={() => shiftMonth(1)} className="cursor-pointer rounded-md border-0 bg-transparent p-1 text-[var(--ink-500)] hover:bg-[var(--line-subtle)]">
              <ChevronRight size={16} aria-hidden />
            </button>
          </div>
          <div className="grid grid-cols-7 gap-y-0.5 text-center">
            {weekdays.map((w, i) => (
              <span key={i} className="pb-1 text-[11px] font-semibold uppercase text-[var(--ink-400)]">{w}</span>
            ))}
            {cells.map((d, i) => {
              if (d === null) return <span key={`b${i}`} />;
              const value = toDay(view.y, view.m, d);
              const past = value < today;
              const selected = value === day;
              return (
                <button
                  key={value}
                  type="button"
                  disabled={past}
                  aria-pressed={selected}
                  onClick={() => save(value)}
                  className={`mx-auto flex h-[34px] w-[34px] items-center justify-center rounded-full border-0 text-[13px] ${
                    selected ? 'bg-[var(--tenant-primary,#111827)] font-semibold text-white' : 'bg-transparent text-[var(--ink-900)] hover:bg-[var(--line-subtle)]'
                  } disabled:cursor-not-allowed disabled:text-[var(--ink-300)] disabled:hover:bg-transparent cursor-pointer`}
                >
                  {d}
                </button>
              );
            })}
          </div>
          <div className="mt-3.5 flex">
            <button type="button" onClick={() => save(null)} className="btn">
              {t('userEstate.noExpiration')}
            </button>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};
